/**
 * FlagIcon — web port of client-native/src/ui/atoms/visual/FlagIcon.tsx.
 *
 * iOS shows FLAGS, never text codes like "ES"/"EN". Use this everywhere a
 * language is represented (dictionary cards, translation popups, settings
 * dialect rows, etc.). SVG paths are copied verbatim from the iOS flag atoms
 * so they render identically.
 *
 *   <FlagIcon countryCode="ES" size="small" />   // Spanish
 *   <FlagIcon countryCode="UK" size="medium" />  // English (UK flag, as iOS)
 *
 * Convenience helpers for the two languages the app uses:
 *   <SpanishFlag />  <EnglishFlag />
 */
import { CSSProperties, FC, memo } from "react";

export type FlagCountry = "ES" | "UK";
export type FlagSize = "small" | "medium" | "large";

export interface FlagIconProps {
  countryCode: FlagCountry;
  size?: FlagSize;
  style?: CSSProperties;
  className?: string;
}

const ASPECT = 2 / 3;
function dims(size: FlagSize) {
  const width = size === "small" ? 21 : size === "large" ? 32 : 28;
  return { width, height: Math.round(width * ASPECT) };
}

const EsFlag: FC<{ width: number; height: number }> = ({ width, height }) => (
  <svg viewBox="0 0 21 15" width={width} height={height}>
    <defs>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="ES_svg__a">
        <stop stopColor="#FFF" offset="0%" />
        <stop stopColor="#F0F0F0" offset="100%" />
      </linearGradient>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="ES_svg__b">
        <stop stopColor="#DD172C" offset="0%" />
        <stop stopColor="#C60B1F" offset="100%" />
      </linearGradient>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="ES_svg__c">
        <stop stopColor="#FFD133" offset="0%" />
        <stop stopColor="#FFC500" offset="100%" />
      </linearGradient>
    </defs>
    <g fill="none" fillRule="evenodd">
      <path fill="url(#ES_svg__a)" d="M0 0h21v15H0z" />
      <path fill="url(#ES_svg__b)" d="M0 0h21v4H0zM0 11h21v4H0z" />
      <path fill="url(#ES_svg__c)" d="M0 4h21v7H0z" />
      <path fill="#FFEDB1" d="M5.5 7h1v.5h-1z" />
      <path
        d="M4.915 8.46c.025.295.291.54.585.54s.56-.246.585-.54L6.207 7H4.793l.122 1.46zm-.624-1.465a.456.456 0 0 1 .465-.495h1.488a.45.45 0 0 1 .465.495l-.126 1.506A1.104 1.104 0 0 1 5.5 9.5a1.104 1.104 0 0 1-1.083-.999L4.29 6.995z"
        fill="#A41517"
        fillRule="nonzero"
      />
      <path
        fill="#A41517"
        d="M4.5 7.5h2V8H6l-.5 1L5 8h-.5zM3 6h1v3.5H3zM7 6h1v3.5H7zM4.5 5.5c0-.276.214-.5.505-.5h.99c.279 0 .505.232.505.5v.248c0 .139-.108.252-.245.252h-1.51a.245.245 0 0 1-.245-.252V5.5z"
      />
    </g>
  </svg>
);

const GbFlag: FC<{ width: number; height: number }> = ({ width, height }) => (
  <svg viewBox="0 0 21 15" width={width} height={height}>
    <defs>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="GB_svg__a">
        <stop stopColor="#FFF" offset="0%" />
        <stop stopColor="#F0F0F0" offset="100%" />
      </linearGradient>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="GB_svg__b">
        <stop stopColor="#0A17A7" offset="0%" />
        <stop stopColor="#030E88" offset="100%" />
      </linearGradient>
      <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="GB_svg__c">
        <stop stopColor="#E6273E" offset="0%" />
        <stop stopColor="#CF152B" offset="100%" />
      </linearGradient>
    </defs>
    <g fill="none" fillRule="evenodd">
      <path fill="url(#GB_svg__a)" d="M0 0h21v15H0z" />
      <path fill="url(#GB_svg__b)" d="M-.002 0h21v15h-21z" />
      <path
        d="M5.003 10H-.002V5h5.005L-2.082.22l1.118-1.657 8.962 6.045V-1h5v5.608l8.962-6.045L23.078.22 15.993 5h5.005v5h-5.005l7.085 4.78-1.118 1.657-8.962-6.045V16h-5v-5.608l-8.962 6.045-1.118-1.658L5.003 10z"
        fill="url(#GB_svg__a)"
      />
      <path
        d="M14.136 4.958l9.5-6.25a.25.25 0 0 0-.275-.417l-9.5 6.25a.25.25 0 1 0 .275.417zM14.868 10.48l8.515 5.74a.25.25 0 1 0 .28-.415l-8.516-5.74a.25.25 0 0 0-.279.415zM6.142 4.526L-2.74-1.461a.25.25 0 0 0-.28.415L5.863 4.94a.25.25 0 0 0 .279-.414zM6.827 9.995l-9.845 6.53a.25.25 0 1 0 .276.416l9.846-6.529a.25.25 0 0 0-.277-.417z"
        fill="#DB1F35"
        fillRule="nonzero"
      />
      <path fill="url(#GB_svg__c)" d="M-.002 9h9v6h3V9h9V6h-9V0h-3v6h-9z" />
    </g>
  </svg>
);

const FlagIcon: FC<FlagIconProps> = ({ countryCode, size = "medium", style, className }) => {
  const { width, height } = dims(size);
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, ...style }}
    >
      {countryCode === "UK" ? <GbFlag width={width} height={height} /> : <EsFlag width={width} height={height} />}
    </span>
  );
};

export const SpanishFlag: FC<Omit<FlagIconProps, "countryCode">> = (p) => <FlagIcon countryCode="ES" {...p} />;
export const EnglishFlag: FC<Omit<FlagIconProps, "countryCode">> = (p) => <FlagIcon countryCode="UK" {...p} />;

export default memo(FlagIcon);
